"use client";
import Image from "next/image";
import { useState, useEffect } from "react";

interface ClientCarouselProps {
  clients: string[];
  intervalMs?: number;
}

export default function ClientCarousel({
  clients,
  intervalMs = 4000,
}: ClientCarouselProps) {
  const [currentIndex, setCurrentIndex] = useState(0);

  // Group clients into sets of 4 (each slide)
  const groupedClients: string[][] = [];
  for (let i = 0; i < clients.length; i += 4) {
    groupedClients.push(clients.slice(i, i + 4));
  }

  // Auto slide
  useEffect(() => {
    if (groupedClients.length <= 1) return;
    const interval = setInterval(() => {
      setCurrentIndex((prev) =>
        prev === groupedClients.length - 1 ? 0 : prev + 1
      );
    }, intervalMs);
    return () => clearInterval(interval);
  }, [groupedClients.length, intervalMs]);

  return (
    <div className="relative w-full max-w-sm mx-auto overflow-hidden">
      {/* Slides */}
      <div
        className="flex transition-transform duration-700 ease-in-out"
        style={{
          transform: `translateX(-${currentIndex * 100}%)`,
        }}
      >
        {groupedClients.map((group, gIdx) => (
          <div
            key={gIdx}
            className={`w-full flex-shrink-0 flex flex-col items-center justify-center gap-4  px-5 transition-opacity duration-500 ${
              gIdx === currentIndex ? "opacity-100" : "opacity-0"
            }`}
            style={{
              display:
                Math.abs(gIdx - currentIndex) <= 1
                  ? "flex"
                  : "none", // hide slides not near currentIndex
            }}
          >
            {group.map((client, i) => (
              <div
                key={i}
                className="rounded-2xl bg-[#DACEC5] flex items-center justify-center w-3/4 h-20 sm:h-24"
              >
                <Image
                  src={client}
                  alt={`Client ${gIdx * 4 + i + 1}`}
                  width={100}
                  height={100}
                  className="object-contain max-h-full py-4"
                  loading="lazy"
                />
              </div>
            ))}
          </div>
        ))}
      </div>

      {/* Pagination Dots */}
      <div className="flex justify-center mt-4 gap-2">
        {groupedClients.map((_, idx) => (
          <button
            key={idx}
            onClick={() => setCurrentIndex(idx)}
            className={`w-2 h-2 rounded-full transition-all ${
              idx === currentIndex ? "bg-[#4F4040] scale-110" : "bg-gray-400"
            }`}
          ></button>
        ))}
      </div>
    </div>
  );
}
